import { type Recipe, type MealKey, mealCaloriePercent, extractGrams, buildScaledRecipeId } from "@/data/recipes";

export interface MacroTarget {
  protein: number;
  carbs: number;
  fat: number;
  cal: number;
}

/**
 * Calculate daily macro targets based on weight, goal, and calories.
 * 
 * | Goal     | Protein       | Fat           | Carbs     |
 * |----------|---------------|---------------|-----------|
 * | Lose     | 2.1 g/kg      | 1.0 g/kg      | remaining |
 * | Maintain | 1.6 g/kg      | 1.1 g/kg      | remaining |
 * | Gain     | 1.9 g/kg      | 1.25 g/kg     | remaining |
 */
export function calcMacroTargets(weight: number, goal: string, calories: number): MacroTarget {
  const protPerKg = goal === "lose" ? 2.1 : goal === "gain" ? 1.9 : 1.6;
  const fatPerKg = goal === "lose" ? 1.0 : goal === "gain" ? 1.25 : 1.1;
  let protein = Math.round(weight * protPerKg);
  let fat = Math.round(weight * fatPerKg);

  // Ensure at least 15% of calories come from carbs.
  // When protein + fat exceed the calorie budget (heavy people, low calories),
  // reduce fat first (to 0.7 g/kg floor), then protein (to 1.2 g/kg floor).
  const minCarbPct = 0.15;
  const maxPFCal = calories * (1 - minCarbPct);
  let pfCal = protein * 4 + fat * 9;
  if (pfCal > maxPFCal) {
    // Step 1: reduce fat
    const fatFloor = Math.round(weight * 0.7);
    const fatReduction = Math.min(fat - fatFloor, Math.ceil((pfCal - maxPFCal) / 9));
    if (fatReduction > 0) {
      fat -= fatReduction;
      pfCal = protein * 4 + fat * 9;
    }
    // Step 2: if still over, reduce protein
    if (pfCal > maxPFCal) {
      const protFloor = Math.round(weight * 1.2);
      const protReduction = Math.min(protein - protFloor, Math.ceil((pfCal - maxPFCal) / 4));
      if (protReduction > 0) {
        protein -= protReduction;
        pfCal = protein * 4 + fat * 9;
      }
    }
    // Step 3: if STILL over (extreme obesity + low cal), force-reduce beyond floors
    if (pfCal > maxPFCal) {
      const excess = pfCal - maxPFCal;
      // Split excess removal: 60% from fat (9cal/g), 40% from protein (4cal/g)
      const fatCut = Math.min(fat - 20, Math.ceil((excess * 0.6) / 9));
      if (fatCut > 0) fat -= fatCut;
      const remaining = (protein * 4 + fat * 9) - maxPFCal;
      if (remaining > 0) {
        const protCut = Math.min(protein - 50, Math.ceil(remaining / 4));
        if (protCut > 0) protein -= protCut;
      }
    }
  }

  const carbsCal = calories - protein * 4 - fat * 9;
  const carbs = Math.max(0, Math.round(carbsCal / 4));
  return { protein, carbs, fat, cal: calories };
}

/** Distribute daily macro targets to a specific meal using calorie percentages */
export function getMealMacroTarget(daily: MacroTarget, mealKey: MealKey): MacroTarget {
  const pct = mealCaloriePercent[mealKey];
  return {
    protein: Math.round(daily.protein * pct),
    carbs: Math.round(daily.carbs * pct),
    fat: Math.round(daily.fat * pct),
    cal: Math.round(daily.cal * pct),
  };
}

/** Distribute daily macros across active meals proportionally */
export function distributeMacrosToMeals(
  daily: MacroTarget,
  activeMeals: MealKey[],
): Record<MealKey, MacroTarget> {
  const totalPct = activeMeals.reduce((s, k) => s + mealCaloriePercent[k], 0);
  const result = {} as Record<MealKey, MacroTarget>;
  for (const key of activeMeals) {
    const pct = mealCaloriePercent[key] / totalPct;
    result[key] = {
      protein: Math.round(daily.protein * pct),
      carbs: Math.round(daily.carbs * pct),
      fat: Math.round(daily.fat * pct),
      cal: Math.round(daily.cal * pct),
    };
  }
  return result;
}

// ─── Helpers ──────────────────────────────────────────

function getRecipeCarbs(r: Recipe): number {
  return r.ingredients.reduce((s, i) => s + i.carbs, 0);
}

function getRecipeFat(r: Recipe): number {
  return r.ingredients.reduce((s, i) => s + i.fat, 0);
}

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

function getMainIngredientGrams(recipe: Recipe): number {
  if (recipe.ingredients.length === 0) return 100;

  // For simple categorized items, use the first ingredient as the base.
  // Composite preparations (prep) and salads should use total measurable grams,
  // otherwise recipes like Rap10 + Carne inherit limits from just the tortilla
  // and get pinned to an inflated minimum scale.
  if (recipe.category && recipe.category !== "salad" && recipe.category !== "prep") {
    const ing = recipe.ingredients[0];
    return ing.quantity_grams || extractGrams(ing.amount) || 100;
  }

  // For salads, prep recipes, and composite/uncategorized recipes, use total
  // weight of all measurable ingredients to avoid distorted scale limits.
  let totalGrams = 0;
  let hasAnyGrams = false;
  for (const ing of recipe.ingredients) {
    const g = ing.quantity_grams || extractGrams(ing.amount);
    if (g && g > 0) {
      totalGrams += g;
      hasAnyGrams = true;
    }
  }
  return hasAnyGrams ? totalGrams : 100;
}

// ─── Gram Limits ──────────────────────────────────────

export function getRecipeGramLimits(recipe: Recipe): { minGrams: number; maxGrams: number } {
  const label = (recipe.id + " " + recipe.label).toLowerCase();
  const ingNames = recipe.ingredients.map(i => i.name.toLowerCase());
  const isStandaloneEgg = recipe.ingredients.length === 1 && ingNames.every(name => name.includes("ovo"));

  if (isStandaloneEgg) {
    return { minGrams: 50, maxGrams: 150 };
  }

  // Optional/sweet single-ingredient treats (chocolate, brigadeiro, paçoca, nutella, doce de
  // leite, leite condensado, bombom). These are tiny indulgence portions: heuristics like
  // "hasMilk" would otherwise force a 80g+ minimum on items that ship as 18-25g servings,
  // inflating optional snacks far above their budget. Cap to a realistic 0.5x-2x window.
  const isOptionalTreat =
    recipe.id.endsWith("_op") &&
    recipe.ingredients.length === 1;
  if (isOptionalTreat) {
    const baseGrams = getMainIngredientGrams(recipe);
    if (baseGrams > 0) {
      return { minGrams: Math.max(5, baseGrams * 0.5), maxGrams: baseGrams * 2 };
    }
  }

  switch (recipe.category) {
    case "protein": return { minGrams: 100, maxGrams: 250 };
    case "carb": return { minGrams: 80, maxGrams: 200 };
    case "legume": return { minGrams: 60, maxGrams: 200 };
    case "salad": {
      const baseGrams = getMainIngredientGrams(recipe);
      return { minGrams: Math.min(50, baseGrams), maxGrams: baseGrams };
    }
  }

  // Bread-based composite (pão de forma + filler like queijo, ovo, peito de
  // peru). The default composite path / label-based "queijo" limit returns
  // ≈80g TOTAL recipe weight, pinning the recipe at 1 slice even when the
  // meal budget asks for 2 slices. Allow scale 0.8x → 2.0x so discrete
  // snapping can naturally land on 1 or 2 slices. Filler ingredients (egg,
  // cheese, requeijão) keep their own ceilings inside `applyScaleToRecipe`
  // so the protein/spread does not also double — only the bread grows.
  const hasPaoDeForma = recipe.ingredients.some((ing) => /pão\s+de\s+forma/i.test(ing.name));
  if (hasPaoDeForma) {
    const totalBase = getMainIngredientGrams(recipe);
    if (totalBase > 0) {
      return { minGrams: totalBase * 0.8, maxGrams: totalBase * 2.0 };
    }
  }

  // Milk-based blended drinks (vitaminas, smoothies). Keep total recipe
  // weight ≤ 300g (regression-tested) but allow modest growth so the meal
  // solver can reach higher kcal targets. Milk itself is capped at 200ml
  // by the ingredient ceiling, so additional scale grows fruit/aveia.
  const isMilkDrink =
    recipe.ingredients.length >= 2 &&
    recipe.ingredients.some((ing) => {
      const n = ing.name.toLowerCase();
      if (!n.includes("leite")) return false;
      const g = ing.quantity_grams || extractGrams(ing.amount);
      return g != null && g >= 100;
    }) &&
    recipe.ingredients.some((ing) => {
      const n = ing.name.toLowerCase();
      return ["banana", "morango", "mamão", "maçã", "abacaxi", "manga", "fruta", "aveia"].some((kw) => n.includes(kw));
    });
  if (isMilkDrink) {
    const totalBase = getMainIngredientGrams(recipe);
    if (totalBase > 0) {
      // Milk itself stays capped at 200ml inside applyScaleToRecipe; the extra
      // headroom (up to 350g total) flows to fruit/aveia so the meal solver can
      // close kcal targets without inflating milk past its realistic ceiling.
      return { minGrams: Math.min(totalBase * 0.6, 200), maxGrams: 350 };
    }
  }

  // Label-based limits only for simple (≤2 ingredient) recipes to avoid
  // misclassifying composites like "panqueca_banana_aveia" as pure aveia
  if (recipe.ingredients.length <= 2) {
    if (label.includes("aveia")) return { minGrams: 20, maxGrams: 80 };
    if (label.includes("granola")) return { minGrams: 15, maxGrams: 80 };
    if (label.includes("pasta_amendoim") || label.includes("pasta de amendoim")) return { minGrams: 10, maxGrams: 40 };
    if ((label.includes(" mel") || label.startsWith("mel_") || label.includes(" mel,")) && !label.includes("melão") && !label.includes("melao") && !label.includes("melancia")) return { minGrams: 5, maxGrams: 20 };
    if (label.includes("queijo")) return { minGrams: 20, maxGrams: 80 };
  }
  if (label.includes("whey")) return { minGrams: 15, maxGrams: 60 };
  if (label.includes("azeite")) return { minGrams: 3, maxGrams: 15 };

  // Inspect ingredients to determine realistic floors for composite recipes
  const hasProtein = ingNames.some(n =>
    n.includes("ovo") || n.includes("frango") || n.includes("carne") ||
    n.includes("peixe") || n.includes("tilápia") || n.includes("salmão") ||
    n.includes("sardinha") || n.includes("peru") || n.includes("lombo")
  );
  const hasYogurt = ingNames.some(n => n.includes("iogurte"));
  const hasMilk = ingNames.some(n => n.includes("leite"));

  // Check if recipe has a liquid base (água, leite) — beverage/juice
  const hasLiquidBase = recipe.ingredients.some(i => {
    const n = i.name.toLowerCase();
    if (!n.includes("água") && !n.includes("leite")) return false;
    const g = i.quantity_grams || extractGrams(i.amount);
    return g != null && g >= 100;
  });

  // Determine base limits by recipe type
  let baseMin: number;
  let baseMax: number;

  if (hasProtein && hasLiquidBase) {
    baseMin = 150; baseMax = 500;
  } else if (hasLiquidBase) {
    const isMillkBased = recipe.ingredients.some(i => i.name.toLowerCase().includes("leite"));
    baseMin = 200; baseMax = isMillkBased ? 300 : 600;
  } else if (hasProtein) {
    baseMin = 80; baseMax = 400;
  } else if (hasYogurt) {
    baseMin = 80; baseMax = 300;
  } else if (hasMilk) {
    baseMin = 80; baseMax = 300;
  } else if (label.includes("iogurte")) {
    baseMin = 80; baseMax = 300;
  } else {
    const fruitKeywords = ["banana", "maçã", "laranja", "melancia", "mamão", "melão", "morango",
      "abacaxi", "uva", "pêra", "kiwi", "caqui", "bergamota", "manga", "fruta"];
    const isFruitRecipe = fruitKeywords.some(kw => label.includes(kw));
    const largeFruits = ["melão", "melancia", "mamão"];
    if (isFruitRecipe) {
      baseMin = largeFruits.some(kw => label.includes(kw)) ? 50 : label.includes("morango") ? 50 : label.includes("banana") ? 50 : 50;
      baseMax = 200;
    } else {
      baseMin = 30; baseMax = 400;
    }
  }

  // For composite recipes, raise minGrams if any individual ingredient
  // would fall below its presentation floor at the current minScale.
  const ING_FLOORS: [string, number][] = [
    ["aveia", 20], ["granola", 15], ["pasta de amendoim", 10], ["queijo", 20], ["cuscuz", 50],
  ];
  const totalBase = getMainIngredientGrams(recipe);
  if (totalBase > 0 && recipe.ingredients.length > 1) {
    for (const ing of recipe.ingredients) {
      const ingGrams = ing.quantity_grams || extractGrams(ing.amount);
      if (!ingGrams || ingGrams <= 0) continue;
      const ingName = ing.name.toLowerCase();
      for (const [kw, floor] of ING_FLOORS) {
        if (!ingName.includes(kw)) continue;
        const neededMinGrams = Math.ceil(totalBase * (floor / ingGrams));
        if (neededMinGrams > baseMin) baseMin = neededMinGrams;
      }
    }
  }

  return { minGrams: baseMin, maxGrams: baseMax };
}

/** Check if a recipe has very low calorie density (< 0.8 cal/g) */
export function isLowCalDensityRecipe(recipe: Recipe): boolean {
  const totalGrams = getMainIngredientGrams(recipe);
  return (recipe.cal / Math.max(1, totalGrams)) < 0.8;
}

export function getScaleLimits(recipe: Recipe): { minScale: number; maxScale: number } {
  const limits = getRecipeGramLimits(recipe);
  const baseGrams = getMainIngredientGrams(recipe);
  if (baseGrams <= 0) return { minScale: 0.5, maxScale: 2.5 };
  return {
    minScale: limits.minGrams / baseGrams,
    maxScale: limits.maxGrams / baseGrams,
  };
}

// ─── Multi-macro scoring ──────────────────────────────

/** Score a combo against a macro target. Lower = better. Protein weighted 2x. */
export function macroScore(
  actual: { protein: number; carbs: number; fat: number },
  target: MacroTarget,
): number {
  return (
    Math.abs(actual.protein - target.protein) * 2 +
    Math.abs(actual.carbs - target.carbs) +
    Math.abs(actual.fat - target.fat)
  );
}

// ─── Structured Meal Solver (lunch/dinner) ────────────

/**
 * Solve per-recipe scale factors for structured meals.
 * Priority: protein → carbs → legumes → salads (unchanged).
 */
export function solveStructuredMealScales(
  combo: Recipe[],
  target: MacroTarget,
): Map<string, number> {
  const scales = new Map<string, number>();

  const proteins = combo.filter(r => r.category === "protein");
  const carbs = combo.filter(r => r.category === "carb");
  const legumes = combo.filter(r => r.category === "legume");
  const salads = combo.filter(r => r.category === "salad");
  const preps = combo.filter(r => r.category === "prep");

  let remProt = target.protein;
  let remCarbs = target.carbs;
  let remFat = target.fat;

  // Step 1: Scale protein sources to hit protein target
  for (const pr of proteins) {
    if (pr.protein <= 0) { scales.set(pr.id, 1); continue; }
    let scale = remProt / pr.protein;
    const { minScale, maxScale } = getScaleLimits(pr);
    scale = clamp(scale, minScale, maxScale);
    scales.set(pr.id, scale);
    remProt -= pr.protein * scale;
    remCarbs -= getRecipeCarbs(pr) * scale;
    remFat -= getRecipeFat(pr) * scale;
  }

  // Step 2: Scale carb sources to hit remaining carbs
  for (const cr of carbs) {
    const crCarbs = getRecipeCarbs(cr);
    if (crCarbs <= 0) { scales.set(cr.id, 1); continue; }
    let scale = Math.max(0, remCarbs) / crCarbs;
    const { minScale, maxScale } = getScaleLimits(cr);
    scale = clamp(scale, minScale, maxScale);
    scales.set(cr.id, scale);
    remProt -= cr.protein * scale;
    remCarbs -= crCarbs * scale;
    remFat -= getRecipeFat(cr) * scale;
  }

  // Step 3: Scale legumes for remaining carbs
  for (const lg of legumes) {
    const lgCarbs = getRecipeCarbs(lg);
    let scale = 1;
    if (lgCarbs > 0 && remCarbs > 5) {
      scale = (remCarbs * 0.4) / lgCarbs;
    }
    const { minScale, maxScale } = getScaleLimits(lg);
    scale = clamp(scale, minScale, maxScale);
    scales.set(lg.id, scale);
    remCarbs -= lgCarbs * scale;
    remFat -= getRecipeFat(lg) * scale;
  }

  // Step 4: Salads stay close to their authored portion
  for (const item of salads) {
    if (!scales.has(item.id)) scales.set(item.id, 1);
  }

  // Step 5: Preps escalonam para preencher as calorias restantes da refeição,
  // respeitando seus limites realistas. Sem isso, receitas "prep" ficavam
  // travadas em scale=1 (ex.: Rap10 + Carne sempre em 250 kcal independente
  // do orçamento da janta).
  if (preps.length > 0) {
    let consumedCal = 0;
    for (const r of combo) {
      if (r.category === "prep") continue;
      const s = scales.get(r.id) ?? 1;
      consumedCal += r.cal * s;
    }
    const remainingCal = Math.max(0, target.cal - consumedCal);
    const totalPrepBaseCal = preps.reduce((s, p) => s + (p.cal || 0), 0);

    for (const pr of preps) {
      const { minScale, maxScale } = getScaleLimits(pr);
      let scale = 1;
      if (totalPrepBaseCal > 0 && remainingCal > 0) {
        const share = (pr.cal || 0) / totalPrepBaseCal;
        const targetCalForPrep = remainingCal * share;
        scale = pr.cal > 0 ? targetCalForPrep / pr.cal : 1;
      }
      scale = clamp(scale, minScale, maxScale);
      scales.set(pr.id, scale);
    }
  }

  return scales;
}

// ─── Simple Meal Solver (breakfast/snack) ─────────────

/**
 * Solve per-recipe scale factors for simple meals.
 * Each recipe is scaled based on its dominant macro contribution.
 */
export function solveSimpleMealScales(
  combo: Recipe[],
  target: MacroTarget,
): Map<string, number> {
  const scales = new Map<string, number>();
  if (combo.length === 0) return scales;

  const baseCal = combo.reduce((s, r) => s + r.cal, 0);
  const uniformScale = baseCal > 0 ? target.cal / baseCal : 1;

  // Pre-compute scale limits and dominant macro per recipe
  const items = combo.map(r => {
    const limits = getScaleLimits(r);
    const rProt = r.protein;
    const rCarbs = getRecipeCarbs(r);
    const rFat = getRecipeFat(r);
    let dominant: "protein" | "carbs" | "fat" | "mixed" = "mixed";
    if (rProt > rCarbs && rProt > rFat) dominant = "protein";
    else if (rCarbs > rProt && rCarbs > rFat) dominant = "carbs";
    else if (rFat > rProt && rFat > rCarbs) dominant = "fat";
    return { r, ...limits, dominant };
  });

  // Step 1: assign initial uniform calorie-based scale to every item.
  // This guarantees that two items of the SAME dominant macro (e.g. cuscuz +
  // banana, both carbs) split calories proportionally to their base size
  // instead of one taking the full macro budget at minScale and the other
  // exploding to maxScale to compensate.
  for (const it of items) {
    let scale = uniformScale;
    scale = clamp(scale, it.minScale, it.maxScale);
    scales.set(it.r.id, scale);
  }

  // Step 2: if items have DIFFERENT dominant macros and there are at most
  // 2 items, refine each toward its dominant macro target (legacy behaviour).
  // This keeps protein items targeting protein when paired with a fruit,
  // without distorting same-group pairings.
  if (combo.length === 2) {
    const [a, b] = items;
    if (a.dominant !== b.dominant && a.dominant !== "mixed" && b.dominant !== "mixed") {
      for (const it of items) {
        let scale = uniformScale;
        if (it.dominant === "protein" && it.r.protein > 0) {
          scale = target.protein / it.r.protein;
        } else if (it.dominant === "carbs" && getRecipeCarbs(it.r) > 0) {
          scale = target.carbs / getRecipeCarbs(it.r);
        }
        scale = clamp(scale, it.minScale, it.maxScale);
        scales.set(it.r.id, scale);
      }
    }
  }

  // Step 3: redistribution pass.
  // After clamping, the actual calorie sum may differ from target.cal.
  // Spread the residual proportionally to items that still have headroom
  // (not at min/max), so any item is not forced to extreme portions.
  for (let iter = 0; iter < 4; iter++) {
    let actualCal = 0;
    const flexible: typeof items = [];
    let flexibleCal = 0;
    for (const it of items) {
      const sc = scales.get(it.r.id) ?? 1;
      const itemCal = it.r.cal * sc;
      actualCal += itemCal;
      // Item is flexible when not pinned at either limit (small epsilon)
      const atMin = sc <= it.minScale + 1e-4;
      const atMax = sc >= it.maxScale - 1e-4;
      if (!atMin && !atMax) {
        flexible.push(it);
        flexibleCal += itemCal;
      }
    }
    const gap = target.cal - actualCal;
    if (Math.abs(gap) <= Math.max(5, target.cal * 0.01)) break;
    if (flexible.length === 0 || flexibleCal <= 0) break;
    const factor = (flexibleCal + gap) / flexibleCal;
    for (const it of flexible) {
      const sc = scales.get(it.r.id) ?? 1;
      const newScale = clamp(sc * factor, it.minScale, it.maxScale);
      scales.set(it.r.id, newScale);
    }
  }

  return scales;
}

/** Apply solved scales to combo IDs, building scaled recipe IDs */
export function applyScales(
  comboIds: string[],
  scales: Map<string, number>,
): string[] {
  return comboIds.map(id => {
    const scale = scales.get(id) ?? 1;
    return buildScaledRecipeId(id, scale);
  });
}
